/**
 * pages/hazard/Detail.js — FR-04 Аюулын дэлгэрэнгүй + шийдвэрлэлт (web)
 * Stitch дизайн: web_2. Дэлгэрэнгүй мэдээлэл, урсгал, шийдвэрлэлтийн форм.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import {
  getHazardById,
  completeHazard,
  rejectHazard,
} from "src/api/hazard";
import {
  useHazardCategoryColors,
  HazardCategoryTag,
} from "src/components/risk/workTypeColors";
import {
  getInspectionType,
  normalizeFaIcon,
} from "src/components/hazard/InspectionTypeSection";
import InspectionLinkBlock from "src/components/hazard/InspectionLinkBlock";
import AssignmentPanel from "src/components/hazard/AssignmentPanel";
import { useUserContext } from "src/contexts/userContext";
// Зөрчлийн гүйцэтгэл / хяналтын бүртгэл — хуучин бүртгэлийн
// Execution + Approve хоёрын орлуулга (хувь, түүх, буцаалт)
import ExecutionLog from "src/components/hazard/ExecutionLog";
import { FULL_ACCESS_GROUPS } from "src/components/inspection/stageAccess";
import CriticalControlSheet from "src/components/inspection/CriticalControlSheet";

const SEVERITY = {
  1: { name: "Бага", cls: "text-risk-low-text bg-risk-low-bg" },
  2: { name: "Дунд", cls: "text-risk-medium-text bg-risk-medium-bg" },
  3: { name: "Өндөр", cls: "text-risk-high-text bg-risk-high-bg" },
  // ⚠ ХУУЧИН 4/5 — сонголтод ГАРАХГҮЙ, зөвхөн баазад хоцорсон мөрийг
  //   «Өндөр» гэж зөв харуулахын тулд үлдээв (ХООСОН харагдах ёсгүй).
  4: { name: "Өндөр", cls: "text-risk-high-text bg-risk-high-bg" },
  5: { name: "Өндөр", cls: "text-risk-high-text bg-risk-high-bg" },
};

const STATUS_NAME = {
  NEW: "Шинэ",
  TRIAGE: "Хяналтад",
  ASSIGNED: "Хуваарилсан",
  IN_PROGRESS: "Хэрэгжиж буй",
  COMPLETED: "Биелсэн",
  REJECTED: "Татгалзсан",
};

function fmt(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("mn-MN");
}

const Detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useUserContext();
  // ХАБЭА хэлтэс (бүлэг 174) — аль ч шалгалтын биелэлтийг
  // баталгаажуулна. Бусад тохиолдолд зөвхөн ТУХАЙН шалгалтыг
  // хийсэн хүн (панель дотор мөр тус бүрд шалгагдана).
  const isHse = String(user?.usergroup || "")
    .split(",")
    .map((g) => g.trim())
    .some((g) => FULL_ACCESS_GROUPS.includes(g));
  const [data, setData] = useState(null);
  const catColors = useHazardCategoryColors(); // лавлахын өнгө (name/id)
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Чухал хяналтын шалгах хуудсыг ЗӨВХӨН ХАРАХ горимоор нээнэ
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getHazardById(id);
      setData(r);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onComplete = async () => {
    if (!action.trim()) {
      Swal.fire({ title: "Хариу арга хэмжээг бичнэ үү", icon: "warning" });
      return;
    }
    setBusy(true);
    try {
      await completeHazard(id, action.trim());
      await Swal.fire({ title: "Шийдвэрлэгдсэн гэж тэмдэглэлээ", icon: "success" });
      load();
    } catch (e) {
      Swal.fire({ title: "Алдаа гарлаа", icon: "error" });
    } finally {
      setBusy(false);
    }
  };

  const onReject = async () => {
    if (!rejectReason.trim()) {
      Swal.fire({ title: "Татгалзах шалтгааныг бичнэ үү", icon: "warning" });
      return;
    }
    setBusy(true);
    try {
      await rejectHazard(id, rejectReason.trim());
      await Swal.fire({ title: "Татгалзлаа", icon: "success" });
      load();
    } catch (e) {
      Swal.fire({ title: "Алдаа гарлаа", icon: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-bg-page p-10 text-center text-text-tertiary">
        <i className="fas fa-spinner fa-spin mr-2" />
        Уншиж байна...
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-bg-page p-10 text-center text-risk-high-text">
        Мэдээлэл олдсонгүй.
      </div>
    );
  }

  const sev = SEVERITY[data.severity] || {};
  const closed = data.status === "COMPLETED" || data.status === "REJECTED";

  /* ── ЭНЭ БИЧЛЭГ АЮУЛ УУ, ЗӨРЧИЛ ҮҮ ────────────────────────────────────
     Нэг хуудас ХОЁР төрлийг үйлчилдэг:
       HZ- = аюулын мэдээлэл  → /hazard/dashboard руу буцна
       ZR- = хяналт шалгалтын зөрчил → /inspection/register руу буцна
     Өмнө нь гарчиг нь үргэлж «Аюулын мэдээлэл», буцах товч нь үргэлж
     аюулын жагсаалт руу очдог байсан тул хяналт шалгалтаас орж ирсэн
     хэрэглэгч огт өөр жагсаалт руу гардаг байв.

     Төрлийг ХОЁР эх сурвалжаар тогтооно: `report_mode` (найдвартай) ба
     дугаарын угтвар (хуучин бичлэгт report_mode хоосон байж болно). */
  const VIOLATION_MODES = ["bn_violation", "hab_dept_violation", "violation"];
  // ⚠ Угтвар нь ХОЁР байж болно: ZR- (зөрчлийн хуудас) ба INS- (хяналт
  //   шалгалтын хуудас). Хоёулаа «Хяналт шалгалт руу» буцна — зөвхөн HZ-
  //   (аюулын мэдээлэл) л аюулын самбар руу очно.
  const isViolation =
    VIOLATION_MODES.includes(data.report_mode) ||
    /^(ZR|INS)/.test(String(data.report_number || "").toUpperCase());

  /* ── БУЦАХ ──────────────────────────────────────────────────────────
     Дараалал: ① `state.from` (дуудсан хуудас илт хэлсэн) → ② БРАУЗЕРЫН
     түүх → ③ төрлөөр нь тааварласан жагсаалт.

     ⚠ ЯАГААД ② ХЭРЭГТЭЙ ВЭ: аюулын дэлгэрэнгүй рүү 10 гаруй хуудаснаас
       ордог (ажлын даалгавар, ӨЭА, миний даалгавар, мэдэгдлийн хонх …).
       Тэдгээрийн зарим нь `from` дамжуулдаггүй тул хэрэглэгч наряд
       даалгавраас ороод буцахад ОГТ ӨӨР газар — аюулын самбар дээр
       гарч ирдэг байв. Түүх нь «хаанаас ирснийг» үргэлж мэднэ.

     ⚠ `history.state.idx` нь react-router-ийн стек дэх байрлал. 0 (эсвэл
       байхгүй) бол энэ хуудас ШУУД хаягаар/шинэ табд нээгдсэн гэсэн үг —
       тэр үед `navigate(-1)` нь аппаас гаргах тул ③ рүү унана. */
  const historyIdx =
    typeof window !== "undefined" ? window.history.state?.idx : undefined;
  const canGoBack = typeof historyIdx === "number" && historyIdx > 0;
  const fallbackTo = isViolation ? "/inspection/register" : "/hazard/dashboard";
  const backTo = location.state?.from || null;
  const goBack = () => {
    if (backTo) navigate(backTo);
    else if (canGoBack) navigate(-1);
    else navigate(fallbackTo);
  };
  const backLabel =
    backTo || canGoBack
      ? "Буцах"
      : isViolation
        ? "Хяналт шалгалт руу"
        : "Жагсаалт руу";

  return (
    <div className="bg-bg-page p-6 text-text-primary">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-page-title text-primary font-bold">
            {data.report_number}
          </h1>
          <p className="text-metadata text-text-tertiary mt-1">
            {isViolation
              ? "Хяналт шалгалтын явцад илэрсэн зөрчлийн дэлгэрэнгүй"
              : "Аюулын мэдээллийн дэлгэрэнгүй"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* ── ЗАСАХ (2026-11-01) ─────────────────────────────────────
              Хэрэглэгч: «оруулсан ажилтан л хэсэгхэн хугацаанд засах
              боломжтой». Тугийг СЕРВЕРЭЭС авна (`is_editable` —
              зохиогч + NEW + 2 минут, `hazard_scope.py::_is_editable`).
              ⚠ Дүрмийг энд дахин бичихгүй: хуулбарлавал товч гараад
                сервер 403 буцаах «урхи» болно. Тугийг буцаахгүй хуучин
                backend дээр товч гарахгүй — ХААЛТТАЙ тал руу буурна. */}
          {data.is_editable ? (
            <button
              onClick={() => navigate(`/hazard/${id}/edit`)}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm"
            >
              <i className="fas fa-pen mr-2" />
              Засах
            </button>
          ) : null}
          <button
            onClick={goBack}
            className="px-4 py-2 rounded-lg border border-border-subtle text-text-secondary hover:bg-surface-container text-sm"
          >
            <i className="fas fa-arrow-left mr-2" />
            {backLabel}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left — info */}
        <div className="col-span-12 lg:col-span-8 space-y-4">
          {/* Summary card */}
          <div className="bg-surface rounded-lg border border-border-subtle shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span
                className={
                  "px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 " +
                  sev.cls
                }
              >
                <i className="fas fa-exclamation-triangle" />
                {sev.name}
              </span>
              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-surface-container text-text-secondary inline-flex items-center gap-1">
                <i className="fas fa-flag" />
                {STATUS_NAME[data.status] || data.status}
              </span>
              {/* Үзлэгийн төрөл (+ шат) — зөрчлийн бүртгэлд л утгатай.
                  Лавлахын өнгөөр: «Чухал хяналт» улаан, «Шат шатны үзлэг» цэнхэр г.м. */}
              {(() => {
                const it = getInspectionType(
                  data.inspection_type || data.inspection_type_id,
                );
                if (!it) return null;
                return (
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1"
                    style={{
                      color: it.color,
                      backgroundColor: (it.color || "#666") + "1A",
                    }}
                  >
                    <i className={"fas " + normalizeFaIcon(it.icon)} />
                    {it.label}
                    {/* Зөрчлийн бүртгэлд (page_kind='violation', төрөл нь
                        «Зөрчил») шат утгагүй — «Зөрчил · 1-р шат» гэж
                        төөрөгдүүлдэг байв. Зөвхөн хяналт шалгалтад гаргана. */}
                    {data.inspection_level &&
                    data.page_kind !== "violation" &&
                    String(data.inspection_type || "").toLowerCase() !== "violation"
                      ? ` · ${data.inspection_level}-р шат`
                      : ""}
                  </span>
                );
              })()}
              {/* Аюулын хүчин зүйл(с) — 1-3 төрөл, лавлахын өнгөт тэмдэглэгээгээр.
                  Эх сурвалж: junction-оос ирсэн `categories` массив; эс бол
                  хуучин denormal `hazard_category_ids`; эс бол ганц үндсэн нэр. */}
              {(() => {
                let cats =
                  Array.isArray(data.categories) && data.categories.length
                    ? data.categories
                    : null;
                if (!cats && data.hazard_category_ids) {
                  cats = String(data.hazard_category_ids)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((id) => ({
                      id,
                      name: catColors.byId[id]?.name || data.hazard_category_name,
                    }));
                }
                if (!cats && data.hazard_category_name) {
                  cats = [
                    {
                      id: data.hazard_category_id,
                      name: data.hazard_category_name,
                    },
                  ];
                }
                if (!cats) return null;
                return cats.map((c, i) => {
                  const color =
                    catColors.byId[c.id]?.color || catColors.byName[c.name];
                  const icon = catColors.byId[c.id]?.icon;
                  return c.name ? (
                    <HazardCategoryTag
                      key={c.id || i}
                      name={c.name}
                      color={color}
                      icon={icon}
                    />
                  ) : null;
                });
              })()}
            </div>
            {/* Аюулын тайлбар — ГОЛ мэдээлэл */}
            <h2 className="text-section-header font-semibold mb-1 whitespace-pre-line">
              {data.description || "Тайлбаргүй"}
            </h2>
            {/* Байршил — бүдэг */}
            <p className="text-metadata text-text-tertiary mb-1">
              <i className="fas fa-map-marker-alt mr-1" />
              {data.location_text ||
                data.affected_location ||
                "Байршил тодорхойгүй"}
            </p>
            <p className="text-metadata text-text-tertiary mb-3">
              <i className="fas fa-user-edit mr-1" />
              {data.insert_user_name || "-"} · {fmt(data.report_date)} ·{" "}
              {data.source === "web" ? "Веб" : "Гар утас"}
            </p>

            {data.voice_file_url ? (
              <div className="mt-3">
                <div className="text-metadata text-text-tertiary mb-1">
                  <i className="fas fa-microphone" /> Оруулсан дуу хоолойн бичлэг
                </div>
                <audio
                  controls
                  preload="none"
                  src={data.voice_file_url}
                  className="w-full max-w-md"
                  style={{ height: 40 }}
                >
                  Таны браузер аудио тоглуулагчийг дэмжихгүй байна.
                </audio>
              </div>
            ) : null}

            {data.photos && data.photos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                {data.photos.map((p) => (
                  <a
                    key={p.id || p.file_url}
                    href={p.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="aspect-square rounded-lg overflow-hidden border border-border-subtle"
                  >
                    <img
                      src={p.file_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            ) : null}

            {/* Чухал хяналтын шалгах хуудас — 15 критик хяналт.
                Зогсоолт гарсан бол УЛААН, эс бөгөөс ногоон. Дэлгэрэнгүйг
                зөвхөн харах горимд нээнэ. */}
            {data.critical_control_sheet ? (
              (() => {
                const s = data.critical_control_sheet.summary || {};
                const stopped = (s.stops || 0) > 0;
                const accent = stopped ? "#A32D2D" : "#16A34A";
                return (
                  <div
                    className="mt-4 rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap"
                    style={{
                      borderColor: accent + "40",
                      backgroundColor: accent + "0D",
                    }}
                  >
                    <div>
                      <div
                        className="text-sm font-semibold inline-flex items-center gap-2"
                        style={{ color: accent }}
                      >
                        <i className="fas fa-shield-alt" />
                        Чухал хяналтын шалгах хуудас
                      </div>
                      <div className="text-metadata text-text-tertiary mt-0.5">
                        Бөглөсөн {s.checked || 0}/{s.total || 0} асуумж
                        {stopped
                          ? ` · Ажил зогсоох эрх эдэлсэн (${s.stops})`
                          : " · Ажил гүйцэтгэх боломжтой"}
                      </div>
                    </div>
                    <button
                      onClick={() => setSheetOpen(true)}
                      className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white"
                      style={{ backgroundColor: accent }}
                    >
                      <i className="fas fa-eye mr-1.5" />
                      Хуудсыг харах
                    </button>
                  </div>
                );
              })()
            ) : null}

            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border-subtle text-sm">
              <div>
                <span className="text-text-tertiary text-metadata block">
                  Өртөх хүний тоо
                </span>
                {data.affected_people_count || "-"}
              </div>
              <div>
                <span className="text-text-tertiary text-metadata block">
                  SLA хугацаа
                </span>
                {fmt(data.sla_deadline)}
              </div>
              {data.gps_lat ? (
                <div className="col-span-2">
                  <span className="text-text-tertiary text-metadata block">
                    GPS байршил
                  </span>
                  <a
                    className="text-primary underline"
                    href={
                      "https://maps.google.com/?q=" +
                      data.gps_lat +
                      "," +
                      data.gps_lng
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {data.gps_lat}, {data.gps_lng}
                  </a>
                </div>
              ) : null}
            </div>
          </div>

          {/* ── ХОЛБОГДОХ ХЯНАЛТ ШАЛГАЛТ ────────────────────────────
              Зөрчил «хаанаас гарсан бэ» гэдгийг эндээс шууд харна:
              хуудасны бөглөлт, илэрсэн зөрчил, хэнд үүрэг өгсөн.
              Жагсаалтын задардаг мөртэй ИЖИЛ компонент. */}
          {data.inspections?.length ? (
            <div className="bg-surface rounded-lg border border-border-subtle shadow-sm p-5">
              <h3 className="text-card-title text-primary font-bold mb-4">
                <i className="fas fa-clipboard-check mr-2" />
                Холбогдох хяналт шалгалт
              </h3>
              <InspectionLinkBlock it={data} />
            </div>
          ) : null}

        </div>

        {/* Right — status timeline */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-surface rounded-lg border border-border-subtle shadow-sm p-5">
            <h3 className="text-card-title text-primary font-bold mb-4">
              Хяналтын явц
            </h3>
            {/* ── ХУВААРИЛАЛТЫН ХУРААНГУЙ (2026-11-02) ─────────────────
                Хэрэглэгч: «Хяналтын явцийн мэдээлэл дээр аль цехийн
                хэнд хувиарласанг харуулах».
                Урсгал нь ХОЁР ШАТТАЙ (ХАБ хэлтэс → БН-ийн ХАБ → цех
                дотроо) тул явцын түүх дангаар «хэн хүлээн авав»-ыг
                хэлдэг ч АЛЬ НЭГЖ гэдгийг хэлдэггүй байв.
                ⚠ `assigned_by_*` нь `hazard_reports`-д БАЙХГҮЙ —
                  сервер `hazard_report_history`-ийн сүүлийн ASSIGNED
                  бичилтээс гаргана (`_attach_assigned_by`).
                ⚠ Байхгүй талбарыг зохиохгүй: хуваарилаагүй мөрд
                  блокыг ОГТ гаргахгүй. */}
            {data.assigned_to_name ||
            data.assigned_to_tn ||
            data.responsible_bn_name ||
            data.responsible_bn_code ? (
              <div className="mb-4 rounded-lg border border-border-subtle bg-surface-container p-3">
                <div className="text-metadata text-text-tertiary mb-1">
                  <i className="fas fa-user-tag mr-1" />
                  Хуваарилалт
                </div>
                {/* ⚠ БН-ИЙГ ГОЛ БОЛГОНО (2026-11-02, хэрэглэгч:
                    «аль БН д хувиарласан нь гарахгүй байна»).
                    `responsible_bn_code` нь нэрнээсээ үл хамааран БН
                    БИШ: 1-р шат нь хүлээн авсан ХҮНИЙ нэгжийг
                    бичдэг тул ихэвчлэн ГРУПП код байдаг (2821 →
                    250104 «ХАБЭА товчоо»). Сервер одоо БН-ийн
                    түвшнийг `responsible_tseh_*`-ээр гаргана
                    (цех 25). Доод нэгжийг ХОЁРДУГААРТ харуулна —
                    хоёулаа хэрэгтэй. */}
                <p className="text-label-bold text-text-primary">
                  {data.responsible_tseh_name ||
                    data.responsible_tseh_code ||
                    data.responsible_bn_name ||
                    data.responsible_bn_code ||
                    "Нэгж тодорхойгүй"}
                </p>
                {(data.responsible_bn_name || data.responsible_bn_code) &&
                String(data.responsible_bn_code || "") !==
                  String(data.responsible_tseh_code || "") ? (
                  <p className="text-metadata text-text-tertiary">
                    <i className="fas fa-sitemap mr-1" />
                    {data.responsible_bn_name || data.responsible_bn_code}
                  </p>
                ) : null}
                <p className="text-sm text-text-secondary">
                  {data.assigned_to_name || data.assigned_to_tn ? (
                    <>Хариуцагч: {data.assigned_to_name || data.assigned_to_tn}</>
                  ) : (
                    <span className="text-risk-medium-text">
                      Хариуцагч хараахан тодроогүй
                    </span>
                  )}
                </p>
                {data.assigned_by_name || data.assigned_by_tn ? (
                  <p className="text-metadata text-text-tertiary mt-1">
                    Хуваарилсан: {data.assigned_by_name || data.assigned_by_tn}
                    {data.assigned_at
                      ? ` · ${String(data.assigned_at).slice(0, 10)}`
                      : ""}
                  </p>
                ) : null}
                {data.sla_deadline ? (
                  <p className="text-metadata text-text-tertiary">
                    Хугацаа: {String(data.sla_deadline).slice(0, 10)}
                  </p>
                ) : null}
              </div>
            ) : null}
            {data.history && data.history.length > 0 ? (
              <ol className="space-y-5">
                {data.history.map((h, i) => {
                  const last = i === data.history.length - 1;
                  return (
                    <li key={h.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div
                          className={
                            "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold " +
                            (last
                              ? "bg-primary text-on-primary"
                              : "bg-surface-container text-text-secondary")
                          }
                        >
                          {i + 1}
                        </div>
                        {!last ? (
                          <div className="w-0.5 flex-1 bg-border-subtle mt-1" />
                        ) : null}
                      </div>
                      <div className="pb-2">
                        <p className="text-label-bold text-text-primary">
                          {STATUS_NAME[h.to_status] || h.to_status}
                        </p>
                        <p className="text-metadata text-text-tertiary">
                          {h.action_user_name || "-"} · {fmt(h.action_date)}
                        </p>
                        {h.note ? (
                          <p className="text-sm text-text-secondary mt-1">
                            {h.note}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-metadata text-text-tertiary">
                Урсгалын мэдээлэл алга.
              </p>
            )}
          </div>

          {/* ⚠ Хэрэглэгчийн хүсэлтээр (2026-09-19) «Шийдвэрлэлтийн тайлан» ба
              «Гүйцэтгэл, хяналт» БАРУУН баганад, «Хяналтын явц»-ын ДООР —
              шийдвэр, гүйцэтгэл, явц гурав нэг талд, зөрчлийн агуулга зүүнд. */}
          {/* Decision form */}
          {!closed ? (
            <div className="bg-surface rounded-lg border border-border-subtle shadow-sm p-5 mt-4">
              <h3 className="text-card-title text-primary font-bold mb-4">
                Шийдвэрлэлтийн тайлан
              </h3>
              <label className="text-label-bold text-text-secondary block mb-2">
                Авсан арга хэмжээ
              </label>
              <textarea
                value={action}
                onChange={(e) => setAction(e.target.value)}
                rows={4}
                placeholder="Аюулыг арилгахад авсан арга хэмжээг дэлгэрэнгүй бичнэ үү..."
                className="w-full bg-surface border border-border-subtle rounded-lg p-3 outline-none focus:ring-2 focus:ring-primary text-sm resize-none mb-4"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={onComplete}
                  disabled={busy}
                  className="px-6 py-2.5 rounded-lg bg-primary text-on-primary text-sm font-semibold hover:bg-primary-dark disabled:opacity-50"
                >
                  <i className="fas fa-check mr-2" />
                  Шийдвэрлэгдсэн гэж тэмдэглэх
                </button>
              </div>

              <div className="mt-5 pt-4 border-t border-border-subtle">
                <label className="text-label-bold text-text-secondary block mb-2">
                  Эсвэл — татгалзах шалтгаан
                </label>
                <div className="flex gap-2">
                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Татгалзах шалтгаан..."
                    className="flex-1 bg-surface border border-border-subtle rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-risk-high-text text-sm"
                  />
                  <button
                    onClick={onReject}
                    disabled={busy}
                    className="px-5 py-2 rounded-lg border border-risk-high-text text-risk-high-text text-sm font-semibold hover:bg-risk-high-bg disabled:opacity-50"
                  >
                    Татгалзах
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-surface rounded-lg border border-border-subtle shadow-sm p-5">
              <h3 className="text-card-title text-primary font-bold mb-2">
                {data.status === "COMPLETED"
                  ? "Шийдвэрлэгдсэн"
                  : "Татгалзсан"}
              </h3>
              <p className="text-sm text-text-secondary whitespace-pre-line">
                {data.response_action || data.rejected_reason || "-"}
              </p>
            </div>
          )}

          {/* ── ГҮЙЦЭТГЭЛ / ХЯНАЛТЫН БҮРТГЭЛ ────────────────────────
              ⚠ Дээрх `response_action` нь НЭГ л текст талбар — хоёр дахь
                удаа засварлавал эхнийхийг дарж бичдэг. Энэ хэсэг нь
                ОЛОН УДААГИЙН гүйцэтгэл, гүйцэтгэлийн ХУВЬ, хянасан
                түүх, «дахин гүйцэтгэх» буцаалтыг тусад нь хөтөлнө
                (хуучин зөрчлийн бүртгэлд байсан боловч шинэ бүтцэд
                шилжээгүй байсан дөрвөн зүйл). */}
          <div className="bg-surface rounded-lg border border-border-subtle shadow-sm p-5 mt-4">
            <ExecutionLog reportId={data.id} myTn={user?.tn} />
          </div>

          {/* ⚠ Даалгавар нь ЯВЦААС тусдаа. «Хяналтын явц» нь бүртгэлийн
              төлөвийн түүх — хэнд юу даалгасныг харуулдаггүй тул
              хэрэглэгч зөрчлийн блок доторх жижиг бичгийг уншдаг байв. */}
          <AssignmentPanel
            data={data}
            myTn={user?.tn}
            isHse={isHse}
            onChanged={load}
          />
        </div>
      </div>

      {/* Чухал хяналтын хуудас — ЗӨВХӨН ХАРАХ (бөглөсөн хариултаа буцаж харна) */}
      {data.critical_control_sheet ? (
        <CriticalControlSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          value={data.critical_control_sheet}
          context={data.critical_control_sheet.context || {}}
          readOnly
        />
      ) : null}
    </div>
  );
};

export default React.memo(Detail);
